import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import * as api from '../lib/api';
import { formatApiError } from '../lib/format';

// Mirrors the server rule exactly (MIN_PASSWORD_LENGTH, and bcrypt's 72-byte
// ceiling on the confirm endpoint). No new policy is invented here.
const MIN_PASSWORD = 6;
const MAX_PASSWORD = 72;

// The inline sanitiser in index.html lifts the token out of the URL before any
// third-party script runs, and hands it over exactly once. Claiming it at module
// scope means it is consumed on the first import and cleared immediately, rather
// than sitting on window until a component happens to mount.
const claimSanitisedToken = () => {
  const take = window.__irTakeRecoveryToken;
  return typeof take === 'function' ? take() : null;
};
const sanitisedToken = claimSanitisedToken();

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The sanitiser is the normal path; the query string is only reached if it did
  // not run (for example a direct in-app navigation that kept the parameter).
  const [token] = useState(() => sanitisedToken || searchParams.get('token') || '');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unusable, setUnusable] = useState(!sanitisedToken && !searchParams.get('token'));
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }
    if (password.length > MAX_PASSWORD) {
      setError(`Password must be ${MAX_PASSWORD} characters or fewer`);
      return;
    }
    if (password !== confirm) {
      setError('Those passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.confirmPasswordReset(token, password);
      // The reset retired every session issued before now, including this
      // browser's if it was signed in, so the stale local one goes too.
      api.clearToken();
      setDone(true);
    } catch (err) {
      // Invalid, expired, malformed and already-used all arrive here as the same
      // 400, and are deliberately not told apart.
      if (err.response?.status === 400) {
        setUnusable(true);
      } else {
        setError(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  if (unusable) {
    return (
      <div className="page signin-page" data-testid="reset-password-page">
        <div className="signin-content">
          <div className="signin-header">
            <span className="welcome-brand">irecommend</span>
            <h1 className="signin-title">This link cannot be used</h1>
            <p className="signin-subtitle" data-testid="reset-link-unusable">
              This link has expired or has already been used. You can request a new one.
            </p>
          </div>
          <button
            type="button"
            className="signin-btn signin-btn-standalone"
            onClick={() => navigate('/forgot-password')}
            data-testid="reset-request-new-link"
          >
            Request a new link
          </button>
          <p className="signin-note">
            <Link className="signin-toggle" to="/signin" data-testid="reset-unusable-signin-link">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="page signin-page" data-testid="reset-password-page">
        <div className="signin-content">
          <div className="signin-header">
            <span className="welcome-brand">irecommend</span>
            <h1 className="signin-title">Password reset</h1>
            <p className="signin-subtitle" data-testid="reset-password-success">
              Your password has been reset. For your security we signed out your other
              devices, so please sign in again with your new password.
            </p>
          </div>
          <button
            type="button"
            className="signin-btn signin-btn-standalone"
            onClick={() => navigate('/signin', { replace: true })}
            data-testid="reset-password-go-to-signin"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page signin-page" data-testid="reset-password-page">
      <div className="signin-content">
        <div className="signin-header">
          <span className="welcome-brand">irecommend</span>
          <h1 className="signin-title">Choose a new password</h1>
          <p className="signin-subtitle">
            Pick something only you would know. This link can only be used once.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="signin-form">
          <div className="signin-field">
            <label className="signin-label" htmlFor="newPassword">New password</label>
            <input
              id="newPassword"
              type="password"
              className={`signin-input ${error ? 'signin-input-error' : ''}`}
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              autoFocus
              data-testid="reset-password-new"
            />
          </div>

          <div className="signin-field">
            <label className="signin-label" htmlFor="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword"
              type="password"
              className={`signin-input ${error ? 'signin-input-error' : ''}`}
              placeholder="Type it again"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(''); }}
              data-testid="reset-password-confirm"
            />
          </div>

          {error && <span className="signin-error" data-testid="reset-password-error">{error}</span>}

          <button
            type="submit"
            className="signin-btn"
            disabled={loading}
            data-testid="reset-password-submit"
          >
            {loading ? 'Please wait…' : 'Save new password'}
          </button>
        </form>

        <p className="signin-note">
          <Link className="signin-toggle" to="/signin" data-testid="reset-password-signin-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};
